import _ from 'lodash';
import async from 'async';
import Utils from '../utils/Utils.js';
import Config from '../utils/Config.js';
import APIService from '../utils/APIService';
import CellModel from '../models/CellModel';
import GameModel from '../models/GameModel';
import Server from '../services/Server';
import DebugDataSetMaker from '../utils/DebugDataSetMaker';
import CellsAnalizer from '../utils/CellsAnalizer';
import SoundManager from '../utils/SoundManager';
import ButtonsManager from '../utils/ButtonsManager';

export default class GameController {

    constructor(game)
    {
        window.gameController = this;
        this._game = game;

        // setup server
        this._server = new Server(leanderGMApi, Config.debug);

        // setup model
        this._model = new GameModel();

        this.READY_TO_PLAY_STATUS = 'READY_TO_PLAY_STATUS';
        this.PLAY_REQUEST_STATUS = 'PLAY_REQUEST_STATUS';
        this.OPEN_PLAY_STATUS = 'OPEN_PLAY_STATUS';
        this.FREE_SPINS_STATUS = 'FREE_SPINS_STATUS';
        this.CLOSE_PLAY = 'CLOSE_PLAY';

        this.status = this.READY_TO_PLAY_STATUS;
    }

    get status()
    {
        return this.__status;
    }

    set status(value)
    {
        this.__status = value;
    }

    /*
    * enter point
    * */
    initialize()
    {
        console.log('~~GameController.initialize');

        const server = this._server;
        const api = server.api;
        const model = this._model;

        server.publish(api.publications.GAME_VERSION_UPDATE, Config.version); // specify game version

        this.subscribeToEvents();

        server.onResponse.add((resp) =>
        {
            const data = resp.data;
            const balanceInfo = data.filter((data) =>
            {
                return data.method === 'openPlay' && data.hasOwnProperty('balance');
            })[0];

            resp.data.forEach((data) =>
            {
                if (['ERROR', 'SERVER_ERROR'].indexOf(data.status) === -1)
                {
                    return;
                }

                const game = this._game;
                const model = this._model;

                switch (data.status)
                {
                    case 'ERROR':
                        game.game.paused = true;
                        console.log('GAME PAUSED. Reason:', data.description);
                        server.throwError(data.code).then((code) =>
                        {
                            game.game.paused = false;
                            console.log('GAME UNPAUSED.');

                            const notFatalErrorCodes = [
                                509, // not enough funds
                                530, // temporarily unavailable
                                531, // responsable gaming: session limit reached
                                532, // responsable gaming: loss limit reached
                                533, // responsable gaming: blocked temporarily
                            ];

                            if (notFatalErrorCodes.indexOf(code) !== -1)
                            {
                                if (balanceInfo)
                                {
                                    model.balance = balanceInfo.balance;
                                }

                                game.unlock(); // lock, but allow pause button when autoSpins > 0
                                this.onStopAutoSpins();
                                this.status = this.READY_TO_PLAY_STATUS;
                            }
                        });
                        break;

                    case 'SERVER_ERROR':
                        game.game.paused = true;
                        server.throwError(data.code).then(() =>
                        {
                            console.log('GAME PAUSED. Reason:', data.description);
                        });
                        break;
                }
            });
        });

        const initPromise = Promise.resolve();
        initPromise.then(() =>
        {
            if (Config.initData)
            {
                return Promise.resolve(Config.initData);
            }

            return server.init();
        }).then((resp) =>
        {
            if (!resp)
            {
                return Promise.reject('Empty init response');
            }

            console.log('initialize confirmed', resp);

            let data = resp.data[0];
            let params = data.params;

            Config.CURRENCY.prefix = params.currencyPrefix || '';
            Config.CURRENCY.suffix = params.currencySuffix || '';
            Config.CURRENCY.decimalPlaces = params.currencyDecimalPlaces;
            const formatting = leanderGMApi.getCurrencyFormatting();
            Config.CURRENCY.groupingSeparator = formatting.groupingSeparator || ',';
            Config.CURRENCY.decimalSeparator = formatting.decimalSeparator || '.';

            Config.PAY_TABLE = (function (payTable)
            {
                const table = {};
                for (let key in payTable)
                {
                    if (!payTable.hasOwnProperty(key)) continue;

                    const payInfo = payTable[key];
                    const ids = [1, 3, 4, 5, 6, 7, 8, 9, 10];
                    if (ids.indexOf(+payInfo.id) === -1) continue;

                    let payValues = payInfo.values;
                    if (!payValues) continue;

                    payValues = Object.keys(payValues)
                        .map((lineCount) =>
                        {
                            if (!payValues[lineCount]) return;
                            return { lines: +lineCount, value: payValues[lineCount] };
                        })
                        .filter((payValue) => !!payValue)
                        .sort((payValue1, payValue2) =>
                        {
                            return payValue2.value - payValue1.value;
                        });

                    table[payInfo.id] = payValues;
                }

                return table;
            })(params.payTable);

            console.log('initial cells', params.noPrizeReelLayout);
            let cellModels = [];
            for (let ii = 0; ii < 3; ++ii) // slot=wheel=jj, cell=row=ii
            {
                for (let jj = 0; jj < 5; ++jj)
                {
                    cellModels.push( new CellModel( Utils.getSymbolByIndex( params.noPrizeReelLayout[ii * 5 + jj], jj, ii ) ) );
                }
            }

            // setup values here
            if (params.autoPlayList.indexOf(0) === -1)
            {
                // add 0 if missing
                params.autoPlayList.unshift(0);
            }

            // Get saved data
            if (data.data && Object.keys(data.data).length)
            {
                Config.savedData = JSON.parse(JSON.stringify(data.data));
            }

            model.initialize({
                currency: params.currencyPrefix,
                availableStakes: params.betPerLine,
                stake: Config.savedData && Config.savedData.hasOwnProperty('stake') ? Config.savedData.stake : params.betPerLineDefault,
                availableAutoSpins: params.autoPlayList,
                autoSpins: 0, //params.autoPlayDefault,
                cellModels: cellModels
            });

            // restore old game model
            if (params.responseHistory)
            {
                this.restoreHistory(params.responseHistory);
            }

            model.balance = data.balance;
            model.onChange.add(this.onGameModelChange, this);

            SoundManager.sound = server.getSound();
            SoundManager.music = server.getMusic();

            // this._server.publish( api.publications.GAME_VERSION_UPDATE, Config.version ); // send version
            server.publish(api.publications.ABOUT_STATUS_CHANGED_GAME, false); // true if about page is visible, false if not
            server.publish(api.publications.PAY_TABLE_STATUS_CHANGED_GAME, false); // true if pay table is visible, false if not
            server.publish(api.publications.CONTROL_PANEL_STATUS_CHANGED_GAME, false); // true if control panel is visible, false if not
            server.publish(api.publications.TOTALBET_UPDATED, model.stake);
            // this._server.publishEvent( api.publications.FULLSCREEN_MODE_ON );

            this.start();

            if (params.responseHistory)
            {
                server.throwError(999)
                .then(() =>
                {
                    const data = params.responseHistory.filter((d) => d.method === 'getBetResult');
                    const spinData = data[data.length-1].params[0];

                    this.onPlay(spinData); // start play
                });
            }
        })
        .catch((err) =>
        {
            console.error('GAME INITIALIZATION ERROR:', err);
        });
    }

    subscribeToEvents()
    {
        const server = this._server;
        const api = server.api;
        const model = this._model;

        window.addEventListener('resize', () =>
        {
            if (!Config.MobileDevice)
            {
                return;
            }

            Config.ORIENTATION = window.innerWidth > window.innerHeight ? Config.LANDSCAPE : Config.PORTRAIT;
        });

        //server.subscribe(api.publications.BROWSER_RESIZED, (event) =>
        //{
        //    if (!Config.MobileDevice)
        //    {
        //        return;
        //    }
        //
        //    Config.ORIENTATION = event.data.orientation;
        //});

        server.subscribe(api.requestStatus.REQUEST_FAILED, (resp) =>
        {
            console.log('REQUEST_FAILED');
            let error = {
                action: function()
                {},
                code: 99,
                confirmLabel: 'RETRY',
                cryticalType: false,
                message: 'Connection Timeout',
                redirect: null
            };

            // Communicate error to LeanderGMApi
            server.publish(api.publications.ERROR_OCCURRED, { data: error.code, error: error });
        });

        server.subscribe(api.publications.DISPOSE_APPLICATION, (resp) =>
        {
            console.log('DISPOSE_APPLICATION');
            server.publish(api.publications.GAME_READY_FOR_UNLOAD);
        });

        server.subscribe(api.publications.MUSIC_STATUS_CHANGED, (resp) =>
        {
            console.log('music changed', resp.data);
            SoundManager.playSound('sound/touch');
            this.updateMusic(!!resp.data);
        });

        server.subscribe(api.publications.SOUND_EFFECTS_STATUS_CHANGED, (resp) =>
        {
            console.log('sfx changed', resp.data);
            SoundManager.playSound('sound/touch');
            this.updateSound(!!resp.data);
        });

        server.subscribe(api.publications.GENERAL_SOUND_STATUS_CHANGED_GAME, (resp) =>
        {
            console.log('general sound changed');
            SoundManager.sound = resp.data;
            SoundManager.playSound('sound/touch');
        });

        server.subscribe(api.publications.BALANCE_UPDATED, (resp) =>
        {
            console.log('BALANCE_UPDATED', resp.data);
            const balance = parseFloat(resp.data.balance) || 0;
            const freeBalance = parseFloat(resp.data.free_balance) || 0;
            model.balance = balance + freeBalance;
            server.publish(api.publications.BALANCE_UPDATED_GAME, {balance, 'free_balance': freeBalance});
        });

        server.subscribe(api.publications.CONTROL_PANEL_STATUS_CHANGED, (resp) =>
        {
            console.log( 'CONTROL_PANEL_STATUS_CHANGED message' );
            if (Config.layout === Config.DESKTOP) // desktop
            {
                this._game._topButtonsPanel._settingsPanel.toggle(resp.data);
            }
            else // mobile
            {
                this._game._panelMenu.toggle(resp.data);
            }
        });

        server.subscribe(api.publications.PAY_TABLE_STATUS_CHANGED, (event) =>
        {
            console.log('PAY_TABLE_STATUS_CHANGED', event);
            if (event && event.data)
            {
                this._game.showHelpPopup();
            }
            else
            {
                this._game.hideHelpPopup();
            }
        });

        server.subscribe(api.publications.GAME_BLOCKED_GUI, (resp) =>
        {
            console.log('BLOCKED GUI', resp);

            if (!!resp.data)
            {
                this.onStopAutoSpins();
            }

            leanderGMApi.__guiBlocked = resp.data;
            game.game.paused = !!resp.data;
        });

        server.subscribe(api.publications.AUTOPLAY_LIMIT_REACHED, (resp) =>
        {
            this._game.unlock();
            this.onStopAutoSpins();
        });
    }

    restoreHistory(responseHistory)
    {
        console.log('restore history', responseHistory);

        const data = responseHistory.filter((d) => d.method === 'getBetResult');
        const last = data[data.length-1];
        const bi = last.params[0].bonusInfo[0];

        this._model.playId = last.playId;
    }

    start()
    {
        let game = this._game;
        let model = this._model;

        game.onPlay.add(this.onPlay, this);
        game.onStopAutoSpins.add(this.onStopAutoSpins, this);
        game.onChangeBet.add((value) => this.model.stake = value);
        game.onChangeAutospins.add((value) =>
        {
            this.model.autoSpins = value;
        });
        game.onGoToLobby.add(() => this._server.goToLobby());

        game.ready();

        // show debug menu in debug mode
        if (Config.debug)
        {
            this.makeDebugMenu();
        }

//        leanderGMApi.subscribeToEvent(leanderGMApi.publications.GAME_BLOCKED_GUI, (resp) =>
//        {
//            console.log('BLOCKED GUI', resp);
//            leanderGMApi.__guiBlocked = resp.data;
//            game.game.paused = !!resp.data;
//        });
    }

    makeDebugMenu()
    {
        if( !Config.debug ) return;

        let game = this._game;
        let debugPanel = game._debugPanel;

        debugPanel.addItems({
            label: 'Wild',
            action: () =>
            {
                this._debugFeature = 'wild';
            }
        });

        debugPanel.addItems({
            label: 'Big Win',
            action: () =>
            {
                this._debugFeature = 'big_win';
            }
        });

        debugPanel.addItems({
            label: '2 Characters',
            action: () =>
            {
                this._debugFeature = '2_characters';
            }
        });

        debugPanel.addItems({
            label: 'High',
            action: () =>
            {
                this._debugFeature = 'high';
            }
        });

        debugPanel.addItems({
            label: 'Scatter',
            action: () =>
            {
                this._debugFeature = 'scatter';
            }
        });

        debugPanel.addItems({
            label: 'Free Spins',
            action: () =>
            {
                this._debugFeature = 'free_spins';
            }
        });

        debugPanel.addItems({
            label: 'Bonus & Lines',
            action: () =>
            {
                this._debugFeature = 'bonus_lines';
            }
        });

        debugPanel.addSeparator();
        debugPanel.addItems({
             label: 'Nudging (down -> top)',
             action: () =>
             {
                this._debugFeature = 'nudging_down_to_top';
             }
        });

        debugPanel.addItems({
            label: 'Nudging (top -> down)',
            action: () =>
            {
                this._debugFeature = 'nudging_top_to_down';
            }
        });

        debugPanel.addSeparator();
        debugPanel.addItems({
            label: 'Clear selection',
            action: () =>
            {
                this._debugFeature = '';
                debugPanel.clearSelection();
            }
        });
    }

    get isFakeFeature()
    {
        return this._debugFeature === 'nudging_down_to_top' || this._debugFeature === 'nudging_top_to_down';
    }

    onPlay(restoreData = null)
    {
        //if (this._debugFeature === 'nudging_down_to_top' ||
        //    this._debugFeature === 'nudging_top_to_down')
        //{
        //    //this.status = this.READY_TO_PLAY_STATUS; // because 'nudging_down_to_top' and 'nudging_top_to_down' use fake requests
        //    this.status = this.OPEN_PLAY_STATUS; // because 'nudging_down_to_top' and 'nudging_top_to_down' use fake requests
        //}
        //else
        if (this.status !== this.READY_TO_PLAY_STATUS)
        {
            return console.error('INVALID STATUS FOR PLAY:', this.status);
        }

        let game = this._game;
        let model = this._model;
        let spinData = null;

        clearTimeout(this._onPlayTimer);

        game.lock(model.autoSpins > 1); // lock, but allow pause button when autoSpins > 0
        game.stopStrokeLinesAnimation(); // stop win line alternation
        model.win = 0; // reset model

        let promises = [];
        if (restoreData) // skip betting when restoring data are available (bet was already done)
        {
            this.status = this.OPEN_PLAY_STATUS;
            promises.push(() =>
            {
                this._server.setGameState(this._server.api.gameStates.OPEN_PLAY);
                return Promise.resolve(restoreData);
            });
        }
        else
        {
            this.status = this.PLAY_REQUEST_STATUS;
            if (model.autoSpins && !this._autospinsOn)
            {
                promises.push(() =>
                {
                    return new Promise((res, rej) =>
                    {
                        this._server.autoSpinConfirmation(
                            model.autoSpins,
                            model.availableStakes.slice().map((value) => value * Config.NUMBER_OF_LINES)
                        ).then((resp) =>
                        {
                            console.log('AUTOPLAY_ACTIVATED_CONFIRMATION', resp);
                            const { spins, totalBet } = resp.data;
                            if (totalBet)
                            {
                                model.stake = Math.floor((totalBet / Config.NUMBER_OF_LINES) * 100) / 100;
                            }
                            model.autoSpins = spins;
                            this._autospinsOn = true;
                            return res();
                        }).catch((resp) =>
                        {
                            console.log('AUTOPLAY_ACTIVATED_CANCELLATION', resp);
                            this._autospinsOn = false;
                            this._game.unlock();
                            this.onStopAutoSpins();
                            this.status = this.READY_TO_PLAY_STATUS;
                            return rej({autoSpinConfirmation: resp});
                        })
                    });
                });
            }

            promises.push(() =>
            {
                return new Promise((res) =>
                {
                    this._server.playRequest() // play request
                    .then((resp) => // bet
                    {
                        console.log('playRequest confirmed');

                        game.game.paused = leanderGMApi.isGUIBlocked();
                        // this is already handled by playRequest
                        if (game.game.paused)
                        {
                            game.game.onResume.addOnce(() =>
                            {
                                res(this.bet());
                            });
                        }
                        else
                        {
                            res(this.bet());
                        }
                    });
                });
            });
        }

        let afterBetPromise = promises.reduce((promise, then) =>
        {
            return promise.then(then);
        }, Promise.resolve());

        afterBetPromise.then((params) => // spin
        {
            game.enableHelpButton(false);

            spinData = params;
            if (model.autoSpins > 0)
            {
                --model.autoSpins;
            }
            game.setAutospins(model.autoSpins);

            return this.spinTo(params);
        })
        .then((creditsWon) => // close play
        {
            return Promise.resolve(creditsWon);
        })
        .then((creditsWon) =>
        {
            // TODO: check condition for big win
            model.win = creditsWon;
            if (model.win > Config.BIGWIN_MULTIPLIER * model.stake * Config.NUMBER_OF_LINES)
            {
                return game.showBigWinPopup(model.win);
            }
            else
            {
                return Promise.resolve();
            }
        })
        .then(() => // finish the play
        {
            if (model.autoSpins > 0)
            {
                const delayBetweenAutospins = 500;

                this._onPlayTimer = setTimeout(() =>
                {
                    this.onPlay();
                }, delayBetweenAutospins);
                return; // no winning lines
            }

            game.unlock(!!model.autoSpins); // unlock
            game.enableHelpButton(true);
        }).
        catch((err) => // errors
        {
            console.log('onPlay error', err);
        });
    }

    bet()
    {
        if (this.status !== this.PLAY_REQUEST_STATUS)
        {
            return Promise.reject(`INVALID STATUS FOR BET: ${this.status}`);
        }

        function processResponse(resp, model)
        {
            let data = resp.data.filter((d) => d.method === 'openPlay')[0];
            model.playId = data.playId;

            data = resp.data.filter((d) => d.method === 'getBetResult')[0];

            if (data && data.hasOwnProperty('balance'))
            {
                model.balance = data.balance;
            }

            const params = data.params[0];

            return params;
        }

        const debugHash = Config.debug ? this.debugBetHash(this._debugFeature) : '';
        if (Config.debug && this._debugFeature && !debugHash)
        {
            return new Promise((res, rej) =>
            {
                this.debugBetResponse(this._debugFeature)
                    .then((resp) =>
                    {
                        const params = processResponse(resp, this._model);
                        this.status = this.OPEN_PLAY_STATUS;
                        res(Promise.resolve(params));
                    })
                    .catch(err =>
                    {
                        console.error('debugBetResponse error:', err);
                        rej(Promise.reject(err));
                    });
            });
        }

        return this._server.bet(this.model.stake, debugHash, {stake: this.model.stake})
        .then((resp) =>
        {
            const params = processResponse(resp, this._model);

            this.status = this.OPEN_PLAY_STATUS;
            return Promise.resolve(params);
        })
        .catch((err) =>
        {
            console.error('bet error:', err);
            return Promise.reject(err);
        });
    }

    closePlay()
    {
        if (this.isFakeFeature)
        {
            this.status = this.READY_TO_PLAY_STATUS;
            return Promise.resolve();
        }

        if (this.status !== this.OPEN_PLAY_STATUS)
        {
            return Promise.resolve();
        }

        if (Config.debug && this._debugFeature && !this.debugBetHash(this._debugFeature))
        {
            return Promise.resolve();
        }

        let game = this._game;
        let model = this._model;

        const server = this._server;
        const api = server.api;

        if (!this.isFakeFeature)
        {
            return server.closePlay(model.playId).
            then((resp) =>
            {
                console.log('play closed', resp);
                const balanceData = resp.data.filter((d) => d.method === 'settlePlay')[0];
                model.balance = parseFloat(balanceData.balance) + parseFloat(balanceData.freeBalance);

                server.publish(api.publications.GAME_RESULTS_SHOWN);

                this.status = this.READY_TO_PLAY_STATUS;
                this._autospinsOn = (model.autoSpins > 0);
                return Promise.resolve();
            }).catch((err) =>
            {
                console.error('closePlay error', err);
                return Promise.reject(err);
            });
        }
        else
        {
            this.status = this.READY_TO_PLAY_STATUS;
            this._autospinsOn = (model.autoSpins > 0);
            return Promise.resolve();
        }
    }

    spinTo(spinData, creditsWon = null, animateStrokeLines = true, creditsWonInSpin = null)
    {
        const game = this._game;
        const model = this._model;
        const server = this._server;

        const reelLayout = spinData.reelLayout;
        const bonusInfo = spinData.bonusInfo;
        const winningLines = spinData.winningLines;
        creditsWon = ( creditsWon === null ? spinData.creditsWon : creditsWon );

        console.log('spin to', reelLayout);
        console.log('bonusInfo', bonusInfo.length, (bonusInfo.length >  0 ? bonusInfo.map((i) => i.bonusName) : ''));
        console.log('winningLines:', winningLines.lines.length, 'creditsWon:', creditsWon);
        console.log('freeSpins', bonusInfo.filter((d) => d.bonusName === 'BonusFreeSpins').length);

        let highlightedReels = Utils.getHighlightedReels(reelLayout, 11/*bonus*/); // get reels that need to shine during the turning

        // calculate cell models
        let cellModels = [];
        for (let ii = 0; ii < reelLayout.length; ii++)
        {
            cellModels.push(new CellModel(Utils.getSymbolByIndex(reelLayout[ii], ii % 5, Math.floor(ii / 5))));
        }
        //console.log('cellModels', cellModels ); //cellModels.map((cm)=> cm.slotIndex+'-'+cm.cellIndex+':'+cm.symbol));

        // is there a free spin bonus
        const bonusFreeSpins = bonusInfo.filter((d) => d.bonusName === 'BonusFreeSpins' ); // free spins bonus
        const bonusWilds = bonusInfo.filter((d) => d.bonusName === 'MatchingPigWild' || d.bonusName === 'Scatter'); // wilds and scatters


        const nudges = bonusInfo.filter((d) => d.bonusName === 'Nudge').pop(); // nudge
        if (nudges)
        {
            nudges.moves.forEach((move) =>
            {
                const slotIndex = move.reel - 1;
                const cells = cellModels.filter((cell) => cell.slotIndex === slotIndex);
                const nudgeCells = move.addSymbols.map((symbol, index) =>
                {
                    return new CellModel(
                        Utils.getSymbolByIndex(symbol, slotIndex, 0)
                    );
                });

                nudgeCells.forEach((cell) =>
                {
                    cellModels.push(cell);
                });

                if (move.direction === 'up')
                {
                    const resultCells = [...cells, ...nudgeCells];
                    resultCells.reduce((index, cell) =>
                    {
                        cell.cellIndex = index;
                        return ++index;
                    }, -nudgeCells.length);
                }
                else if (move.direction === 'down')
                {
                    const resultCells = [...nudgeCells, ...cells];
                    resultCells.reduce((index, cell) =>
                    {
                        cell.cellIndex = index;
                        return ++index;
                    }, 0);
                }
            });
        }

        let spinPromise;
        if (Config.alwaysTurbo)
        {
            spinPromise = game.setCells( cellModels );
        }
        else if (bonusFreeSpins.length && Config.savedData && Config.savedData.freeSpinsLeft)
        {
            spinPromise = Promise.resolve();
        }
        else
        {
            spinPromise = game.spinTo(cellModels, highlightedReels);
        }

        return spinPromise.then(() => // nudge reels
        {
            // nudging
            const nudges = bonusInfo.filter((d) => d.bonusName === 'Nudge'); // nudge
            if (!nudges.length)
            {
                return Promise.resolve();
            }

            return Promise.resolve(game.nudging());
        })
        .then(() =>
        {
            // update win
            // show win
            if (!bonusFreeSpins.length)
            {
                if (creditsWonInSpin !== null)
                {
                    model.win = creditsWonInSpin;
                }

                game.bonusWin = creditsWon;
            }

            const promiseChain = [
                () =>
                {
                    if (bonusFreeSpins.length)
                    {
                        this.status = this.FREE_SPINS_STATUS;
                        return Promise.resolve();
                    }
                    else
                    {
                        return this.closePlay();
                    }
                },
                () =>
                {
                    const hasBigWin = spinData.creditsWon > Config.BIGWIN_MULTIPLIER * this._model.stake * Config.NUMBER_OF_LINES;
                    const hasFreeSpins = !!bonusFreeSpins.length;
                    if (!hasFreeSpins && !hasBigWin)
                    {
                        game.unlock(!!model.autoSpins);
                    }

                    return Promise.resolve();
                }
            ];

            // highlight wild and scatter symbols (resize if needed)
            // check bonus results
            //let bonusWilds = bonusInfo.filter((d) => d.bonusName === 'MatchingPigWild' || d.bonusName === 'Scatter'); // wilds and scatters
            if (bonusWilds.length)
            {
                // animate scatter and wild symbols
                const cells = [];
                for (let ii = 0; ii < bonusWilds.length; ++ii)
                {
                    let type = (bonusWilds[ii].bonusName === 'MatchingPigWild' ? 'wild': 'scatter');
                    const reels = (type === 'wild' ? bonusWilds[ii].wildReels : bonusWilds[ii].scatterReels);
                    for (let jj = 0; jj < reels.length; ++jj)
                    {
                        let cs = cells.filter((c) => c.slotIndex === reels[jj].reel - 1);
                        let cell = (cs.length > 0 ? cs[0] : new CellModel({
                            symbol: ['singer_extra', 'guy_extra', 'old_extra'][reels[jj].pig - 1],
                            type: 'extra',
                            highlight: [],
                            slotIndex: reels[jj].reel - 1,
                            cellIndex: 0
                        }));
                        cell.highlight.push(type);

                        if (!cs.length)
                        {
                            cells.push(cell);
                        }
                    }
                }

                promiseChain.push(() => game.transformTo(cells));
            }

            // winning lines
            // show winning lines
            if (winningLines.lines.length)
            {
                const lines = winningLines.lines;
                let lineNumbers = lines.map((line) => line.line - 1);

                let cells = [];
                for (let ii = 0; ii < lines.length; ++ii)
                {
                    cells.push.apply(cells, lines[ii].matchPositions);
                }
                cells = _.uniq(cells);

                cells.forEach((cell) =>
                {
                    let cellModel = Utils.findCellModel(cellModels, (cell - 1) % 5, Math.floor((cell - 1) / 5));
                    if (cellModel === null)
                    {
                        cellModel = Utils.findCellModel(cellModels, (cell - 1) % 5, 0);
                    } // transformed
                    cellModel.highlight = true;
                });

                promiseChain.push(() => game.strokeLines(lineNumbers));
                promiseChain.push(() => lineNumbers.length > 1 ? Utils.makeTimeoutPromise(2000) : Promise.resolve());
                promiseChain.push(() => game.highlightCells(cellModels));

                if (animateStrokeLines)
                {
                    promiseChain.push(() => game.animateStrokeLines(lineNumbers));
                }
            }

            return promiseChain.reduce((promise, then) =>
            {
                return promise.then(then);
            }, Promise.resolve());
        })
        .then(() => // free spins
        {
            if (!bonusFreeSpins.length)
            {
                return Promise.resolve();
            }

            let bfs = bonusFreeSpins[0];

            return new Promise((res) =>
            {
                if (Config.savedData && Config.savedData.freeSpinsLeft)
                {
                    res(Promise.resolve());
                }
                else
                {
                    res(game.showBonusSpinPopup(bfs.cantFreeSpins));
                }
            })
            .then(() =>
            {
                game.onPlay.remove(this.onPlay, this);
                game.onStopAutoSpins.remove(this.onStopAutoSpins, this);

                //let paused = false;
                //
                //const onPlay = () =>
                //{
                //    paused = false;
                //    game.enablePauseButton();
                //};
                //
                //const onPause = () =>
                //{
                //    paused = true;
                //    game.enablePlayButton();
                //};
                //
                //game.onPlay.add(onPlay);
                //game.onStopAutoSpins.add(onPause);

                game.showBonusBackground();
                let autoSpins = bfs.cantFreeSpins;
                game.bonusSpins = autoSpins;
                game.lock(true); // locked without pause button

                const bonusSpinsPromise = new Promise((res) =>
                {
                    const freeSpinsLeft = Config.savedData && Config.savedData.freeSpinsLeft ? Config.savedData.freeSpinsLeft : 0;
                    model.autoSpins = freeSpinsLeft || bfs.cantFreeSpins;
                    const freeSpins = freeSpinsLeft ?
                        bfs.freeSpinList.slice(Math.max(bfs.freeSpinList.length - freeSpinsLeft - 1, 0)) :
                        bfs.freeSpinList.slice();
                    let autoplayTimer = null;

                    if (freeSpinsLeft)
                    {
                        let prevWon = bfs.freeSpinList.slice(0, Math.max(bfs.freeSpinList.length - freeSpinsLeft)).reduce((prev, next) =>
                        {
                            return prev + next[0].creditsWon;
                        }, 0);

                        if (prevWon)
                        {
                            model.win = 0;
                            game.bonusWin = prevWon;
                        }
                    }

                    async.whilst(
                        () => freeSpins.length,
                        (cbk) =>
                        {
                            const freeSpin = freeSpins.shift();
                            game.onPlay.removeAll();
                            game.onStopAutoSpins.removeAll();
                            clearTimeout(autoplayTimer);

                            const onPlay = () =>
                            {
                                game.onPlay.removeAll();
                                game.lock();

                                let playPressed = false;
                                game.onPlay.addOnce(() =>
                                {
                                    playPressed = true;
                                    cbk();
                                });

                                game.bonusSpins = freeSpins.length;
                                server.saveData({freeSpinsLeft: freeSpins.length});
                                game.stopStrokeLinesAnimation();

                                const creditsWon = freeSpin[0].creditsWon;
                                const creditsWonAccumulated = freeSpin[0].creditsWonAccumulated;

                                this.spinTo(freeSpin[0], creditsWonAccumulated + creditsWon, false, creditsWon)
                                    .then(() =>
                                    {
                                        if (playPressed)
                                        {
                                            return;
                                        }

                                        const delayBetweenAutospins = 500;

                                        autoplayTimer = setTimeout(() =>
                                        {
                                            game.onPlay.removeAll();
                                            cbk();
                                        }, delayBetweenAutospins);
                                        game.onPlay.addOnce(() =>
                                        {
                                            clearTimeout(autoplayTimer);
                                            cbk();
                                        });
                                    });
                            };

                            const onPause = () =>
                            {
                                // immediate stop reels not implemented
                            };

                            game.onPlay.add(onPlay);
                            game.onStopAutoSpins.add(onPause);

                            onPlay();
                        },
                        () =>
                        {
                            game.onPlay.removeAll();
                            game.onStopAutoSpins.removeAll();

                            // restore signal listeners
                            game.onPlay.add(this.onPlay, this);
                            game.onStopAutoSpins.add(this.onStopAutoSpins, this);

                            this.onStopAutoSpins();

                            server.saveData({freeSpinsLeft: 0});
                            if (Config.savedData && Config.savedData.hasOwnProperty('freeSpinsLeft'))
                            {
                                Config.savedData.freeSpinsLeft = 0;
                            }

                            res(Promise.resolve());
                        }
                    );
                });

                return bonusSpinsPromise;
            })
            .then(() =>
            {
                game.setCells(cellModels); // set cells to first round values
                game.strokeLines(winningLines.lines.map((line) => line.line-1 )); // stroke lines, no auto hide
                game.highlightCells(cellModels); // highlight winning cells

                return game.showEndBonusRoundPopup(bfs.creditsWon).then(() =>
                {
                    this.status = this.OPEN_PLAY_STATUS;
                    return this.closePlay();
                });
            })
            .then(() =>
            {
                game.hideBonusBackground();
                //return Promise.resolve();
            });
        }) // resolve with credits won
        .then(() =>
        {
            return Promise.resolve(spinData.creditsWon);
        }) // log error
        .catch((err) =>
        {
            console.error('Spin error:', err);
        });
    }

    onStopAutoSpins()
    {
        const model = this.model;
        this._game.setAutospins(model.autoSpins = model.minAutoSpins);
        this._autospinsOn = false;

        const server = this._server;
        server.publish(server.api.publications.AUTOPLAY_CANCELLED);
    }

    get model()
    {
        return this._model;
    }

    onGameModelChange(model, propertyName, value)
    {
        const server = this._server;
        const api = server.api;
        const game = this._game;

        switch (propertyName)
        {
            case 'balance':
                this._game._footer.updateBalance(value);
                break;

            case 'stake':
                server.publish(api.publications.TOTALBET_UPDATED, value * Config.NUMBER_OF_LINES);
                game._footer.updateBet(value);
                game.setStake(value);
                model.win = 0;
                break;

            case 'win':
                server.publish(api.publications.TOTALWIN_UPDATED, value);
                game._winPanel.value = value;
                break;
        }
    }

    updateMusic(value)
    {
        SoundManager.music = value;

        if (Config.layout === Config.DESKTOP)
        {
            this._game._topButtonsPanel._settingsPanel._musicCheckBox.checked = SoundManager.music;
        }
        else
        {
            this._game._panelMenu._musicCheckBox.checked = SoundManager.music;
        }
    }

    updateSound(value)
    {
        SoundManager.sound = value;

        if (Config.layout === Config.DESKTOP)
        {
            this._game._topButtonsPanel._settingsPanel._soundCheckBox.checked = SoundManager.sound;
        }
        else
        {
            this._game._panelMenu._soundCheckBox.checked = SoundManager.sound;
        }
    }

    debugBetResponse(feature = 'default')
    {
        if (feature === 'test_request')
        {
            feature = 'default';
        }

        return fetch(`/assets/debug/responses/${feature}.json`)
            .then(resp => resp.json())
            .then(json => Promise.resolve(json));
    }

    debugBetHash(feature)
    {
        switch (feature)
        {
            case '2_characters':
                return '53101808053';

             case 'big_win':
                return '2620537773';

            case 'bonus_lines':
                return '6628282633006051105035019075037094031056084104079106112014047048048104010043102025046082026093084048087044075094085114055040113093051110014001114097062104104030464003200500442';

            case 'free_spins':
                return '6202125380030061077061105025019117042011040009062029020092116078095047053113050095045030079025015094014058051096117073117062045029031068083043062102098008068096202140110422243';

            case 'high':
               return '5639697784';

            case 'scatter':
                return '1716534351';

            case 'wild':
                return '5358355524';

            default:
                return '';
        }
    }
}